/**
 * NotificationPermissionCard — iter134t
 *
 * Small self-contained card that shows the current push-notification
 * permission state and lets the user re-request permission or deep
 * link to the OS Settings page (the ONLY escape hatch after a
 * previous "Don't allow" tap — Android 13+/iOS suppress re-prompts).
 *
 * Renders NOTHING on web platforms — push isn't wired for web yet.
 * Renders a compact card on native (customer/rider/merchant apps).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Loader2, ExternalLink, CheckCircle2, AlertCircle } from "lucide-react";
import {
    ensurePushRegistration,
    getPushPermissionStatus,
    onAppResume,
    requestPushPermission,
    openAppNotificationSettings,
} from "../lib/capacitor";
import { needsPermissionRecheck } from "../lib/notificationPermission";

export default function NotificationPermissionCard() {
    const [status, setStatus] = useState(null);    // {supported, state, token, ...}
    const [busy, setBusy] = useState(false);

    const refresh = async () => setStatus(await getPushPermissionStatus());
    useEffect(() => { refresh(); }, []);

    // Back from Settings with notifications switched on: register and clear the warning now, not after the app is
    // restarted — the same re-check CustomerHeader does for location. Only while the warning is on screen, so a
    // device that's already fine makes no extra calls. ensurePushRegistration never prompts; it only registers
    // when the OS now allows it.
    const warning = !!status?.supported && needsPermissionRecheck(status?.state);
    useEffect(() => {
        if (!warning) return undefined;
        let unsubscribe = () => {};
        let unmounted = false;
        onAppResume(async () => {
            await ensurePushRegistration().catch(() => {});
            if (!unmounted) setStatus(await getPushPermissionStatus());
        }).then((off) => { if (unmounted) off(); else unsubscribe = off; });
        return () => { unmounted = true; unsubscribe(); };
    }, [warning]);

    if (!status) return null;
    // Web — don't confuse browser users with a native-only affordance.
    if (!status.supported && status.state === "web") return null;

    const state = status.state;
    const enable = async () => {
        setBusy(true);
        try {
            const r = await requestPushPermission();
            const newState = r.state;
            if (newState === "granted") {
                toast.success("Push notifications enabled — you're all set.");
            } else if (newState === "denied") {
                // The OS suppressed the prompt because the user
                // previously said no. Direct them to Settings.
                toast.warning(
                    "Push access was blocked earlier. Opening system settings so you can turn it on.",
                    { duration: 6000 },
                );
                await openAppNotificationSettings();
            } else if (newState === "prompt") {
                toast.info("Please respond to the permission dialog.");
            }
            await refresh();
        } finally {
            setBusy(false);
        }
    };

    const openSettings = async () => {
        const opened = await openAppNotificationSettings();
        if (!opened) {
            toast.error("Couldn't open settings. Please open Settings → Apps → Notifications manually.");
        }
    };

    // Render — three visual states.
    if (state === "granted") {
        return (
            <div
                data-testid="notif-permission-card"
                className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-start gap-3"
            >
                <CheckCircle2 size={20} className="shrink-0 mt-0.5 text-emerald-600" />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-emerald-800">
                        Push notifications are on
                    </p>
                    <p className="text-xs text-emerald-700/80 mt-0.5">
                        You&apos;ll receive order alerts, delivery updates, and offers.
                    </p>
                    {status.token && (
                        <p className="mt-1 text-[10px] font-mono text-emerald-700/60 truncate">
                            device: …{status.token.slice(-12)}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const isDenied = state === "denied";
    return (
        <div
            data-testid="notif-permission-card"
            className={`rounded-2xl border p-4 ${isDenied
                ? "border-red-500/40 bg-red-500/5"
                : "border-primary/40 bg-primary/5"
            }`}
        >
            <div className="flex items-start gap-3">
                {isDenied
                    ? <BellOff size={20} className="shrink-0 mt-0.5 text-red-600" />
                    : <Bell size={20} className="shrink-0 mt-0.5 text-primary" />}
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">
                        {isDenied ? "Notifications are turned off" : "Turn on notifications"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {isDenied
                            ? "You previously blocked notifications for this app. Enable them from system settings to get order alerts."
                            : "Get real-time order alerts, delivery updates and offers on your phone."}
                    </p>
                </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
                {!isDenied && (
                    <button
                        data-testid="enable-notifications-btn"
                        onClick={enable}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-xs font-bold active:scale-95 transition-transform disabled:opacity-60"
                    >
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <Bell size={13} />}
                        {busy ? "Requesting…" : "Enable notifications"}
                    </button>
                )}
                <button
                    data-testid="open-notif-settings-btn"
                    onClick={openSettings}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold active:scale-95 transition-transform ${isDenied
                        ? "border-red-600 text-red-700 bg-white"
                        : "border-border bg-background"
                    }`}
                >
                    <ExternalLink size={13} /> Open Settings
                </button>
            </div>
            {status.error && (
                <div className="mt-2 flex items-start gap-1.5 text-[11px] text-red-700">
                    <AlertCircle size={12} className="shrink-0 mt-0.5" />
                    <span className="truncate">{status.error}</span>
                </div>
            )}
        </div>
    );
}
